import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { contarPendientes } from "../lib/offlineDb";
import { sincronizarPendientes } from "../lib/sincronizacion";

interface OfflineContextValue {
  enLinea: boolean;
  pendientes: number;
  sincronizando: boolean;
  ultimaSincronizacion: Date | null;
  sincronizarAhora: () => void;
}

const OfflineContext = createContext<OfflineContextValue | null>(null);

export function OfflineProvider({ children }: { children: ReactNode }) {
  const [enLinea, setEnLinea] = useState(navigator.onLine);
  const [pendientes, setPendientes] = useState(0);
  const [sincronizando, setSincronizando] = useState(false);
  const [ultimaSincronizacion, setUltimaSincronizacion] = useState<Date | null>(null);

  const actualizarConteo = useCallback(() => {
    contarPendientes().then(setPendientes).catch(() => {});
  }, []);

  const sincronizarAhora = useCallback(async () => {
    if (!navigator.onLine) return;
    setSincronizando(true);
    try {
      await sincronizarPendientes();
      setUltimaSincronizacion(new Date());
    } finally {
      setSincronizando(false);
      actualizarConteo();
    }
  }, [actualizarConteo]);

  useEffect(() => {
    actualizarConteo();

    function alVolverEnLinea() {
      setEnLinea(true);
      sincronizarAhora();
    }
    function alPerderConexion() {
      setEnLinea(false);
    }
    window.addEventListener("online", alVolverEnLinea);
    window.addEventListener("offline", alPerderConexion);
    window.addEventListener("mascotasvet:pendientes-cambiaron", actualizarConteo);

    // Respaldo por si el navegador no dispara "online" de forma confiable
    // (pasa en algunos equipos/redes): revisa la cola cada 30s y, si hay
    // conexión y pendientes, intenta vaciarla sola.
    const intervalo = setInterval(() => {
      if (navigator.onLine) sincronizarAhora();
    }, 30000);

    return () => {
      window.removeEventListener("online", alVolverEnLinea);
      window.removeEventListener("offline", alPerderConexion);
      window.removeEventListener("mascotasvet:pendientes-cambiaron", actualizarConteo);
      clearInterval(intervalo);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OfflineContext.Provider value={{ enLinea, pendientes, sincronizando, ultimaSincronizacion, sincronizarAhora }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline(): OfflineContextValue {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error("useOffline debe usarse dentro de <OfflineProvider>");
  return ctx;
}
